/*
 * Overview — the synthesised single pane (Parts I–II).
 *
 * Three honest layers, top to bottom:
 *   1. The "living mesh" — a dependency-free canvas node-map. Agencies are
 *      nodes, cross-signs are edges, signed packets are data-in-motion (ember).
 *      Frame-rate-independent (dt-driven); prefers-reduced-motion renders ONE
 *      still frame and never animates. The packet RATE is bound to the live
 *      throughput delta — it is a visualisation of real data, not decoration.
 *   2. The plane STACK — the five planes as layered strata of one substrate,
 *      in architecture order (Trust → Transport → Catalog → Observability →
 *      Consent). Transport is the one LIVE, lit band (driven by /healthz +
 *      /v1/metrics). The other four bind to their namespaced client fns, which
 *      reject with PlaneOfflineError → rendered as CALM dormant strata (an
 *      expected pre-pilot state, not a failure). A plane that responds
 *      UNEXPECTEDLY (kind:"error") is the only thing rendered LOUD red.
 *   3. Top-line throughput / queue / verification, straight from the poller.
 *
 * Provability law: every figure here is inspectable. The verification badge
 * expands to its evidence; each metric tile links to the /v1/metrics line it
 * was parsed from. Nothing is asserted that cannot be substantiated from live
 * data, and offline planes are never faked.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Claim,
  MetricSample,
  PlaneId,
  Role,
  VerifyState,
} from "../api/types";
import { TRANSPORT_METRICS } from "../api/types";
import {
  trust,
  catalog,
  consent,
  audit,
  isPlaneOffline,
  isMeshApiError,
  MESH_BASE,
  PlaneOfflineError,
} from "../api/client";
import {
  useMetricsPoll,
  useDerivedSeries,
  sumMetric,
  findMetric,
} from "../api/metrics";
import { useHealthPoll } from "../components/AppShell";
import { MetricTile } from "../components/MetricTile";
import { PlaneState } from "../components/PlaneState";
import { PlaneStratum } from "../components/PlaneStratum";
import type { StratumState } from "../components/PlaneStratum";
import { VerifyBadge } from "../components/VerifyBadge";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";

/* ====================================================================== *
 * Plane-health model — the five planes, with which client fn probes them. *
 * ====================================================================== */

interface PlaneRow {
  id: PlaneId;
  label: string;
  plane: string;
  /** Section ref for the offline panel. */
  specRef: string;
  /** Endpoints this plane binds to (used in the offline disclosure). */
  endpoints: string[];
  /** Classify the plane's live business read into an honest PlaneRowStatus. */
  classify: () => Promise<PlaneRowStatus>;
}

/**
 * The plane stack, top-to-bottom in ARCHITECTURE ORDER. These ARE the
 * substrate's layers, read as a stack — Transport is the one live band, the
 * rest come from PLANE_ROWS below (each now a real, live read).
 */
const PLANE_ORDER: PlaneId[] = [
  "trust", // Plane III
  "transport", // Plane IV — LIVE
  "catalog", // Plane V
  "observability", // Plane VI
  "consent", // Plane VII
];

/**
 * Honest outcome of one plane's live business read:
 *
 *   live    — the service answered, with or without data. Covers a normal
 *             result, a correctly-empty result (§16.4 "no anchor yet", an
 *             empty ledger), AND a correctly-gated 403 (consent, §39.3.1) —
 *             all three mean "the service is live and functioning correctly".
 *   offline — genuinely unreachable (network failure or 502 from the proxy).
 *   error   — an unexpected, unclassified failure. The one loud red case.
 */
type PlaneRowStatus =
  | { kind: "loading" }
  | { kind: "live"; badgeState: VerifyState; badgeLabel: string; metrics: { label: string; value: string }[] }
  | { kind: "offline"; err: PlaneOfflineError }
  | { kind: "error"; message: string };

/** The four non-Transport planes, each classified from a real, live read. */
const PLANE_ROWS: PlaneRow[] = [
  {
    id: "trust",
    label: "Trust",
    plane: "Plane III · §15–§23",
    specRef: "Part III §15–§23",
    endpoints: ["GET /v1/registry/participants", "GET /v1/log/sth"],
    classify: async () => {
      try {
        const sth = await trust.getSignedTreeHead();
        return {
          kind: "live",
          badgeState: "verified",
          badgeLabel: "✓ LIVE",
          metrics: [
            { label: "tree size", value: sth.tree_size.toLocaleString() },
            { label: "anchor", value: sth.anchor_date },
          ],
        };
      } catch (e) {
        if (isPlaneOffline(e)) return { kind: "offline", err: e };
        if (isMeshApiError(e) && e.status === 404) {
          return {
            kind: "live",
            badgeState: "attested",
            badgeLabel: "LIVE · no anchor yet",
            metrics: [{ label: "status", value: "no anchor published" }],
          };
        }
        return { kind: "error", message: e instanceof Error ? e.message : "probe failed" };
      }
    },
  },
  {
    id: "catalog",
    label: "Catalog",
    plane: "Plane V · §32–§36",
    specRef: "Part V §32–§36",
    endpoints: ["GET /v1/catalog/services"],
    classify: async () => {
      try {
        const r = await catalog.listServices();
        return {
          kind: "live",
          badgeState: "verified",
          badgeLabel: "✓ LIVE",
          metrics: [{ label: "published", value: String(r.items.length) }],
        };
      } catch (e) {
        if (isPlaneOffline(e)) return { kind: "offline", err: e };
        return { kind: "error", message: e instanceof Error ? e.message : "probe failed" };
      }
    },
  },
  {
    id: "observability",
    label: "Audit ledger",
    plane: "Plane VI · §37–§41",
    specRef: "Part VI §37–§41",
    endpoints: ["GET /v1/audit/entries", "GET /v1/audit/verify"],
    classify: async () => {
      // Cheap probe by design: /v1/audit/entries is limit-capped, while
      // /v1/audit/verify re-verifies a whole window cryptographically —
      // the homepage must never auto-trigger crypto work on every load.
      // Chain verdicts (with the loud red for a real divergence) live in
      // the Observability view's explicit verify panel.
      try {
        const r = await audit.listEntries();
        const empty = r.head_seq === null;
        return {
          kind: "live",
          badgeState: empty ? "attested" : "verified",
          badgeLabel: empty ? "LIVE · empty ledger" : "✓ LIVE",
          metrics: [
            { label: "tree_size", value: String(r.tree_size) },
          ],
        };
      } catch (e) {
        if (isPlaneOffline(e)) return { kind: "offline", err: e };
        return { kind: "error", message: e instanceof Error ? e.message : "probe failed" };
      }
    },
  },
  {
    id: "consent",
    label: "Consent",
    plane: "Plane VII · §42–§47",
    specRef: "Part VII §42–§47",
    endpoints: ["GET /v1/consent/records"],
    classify: async () => {
      try {
        const r = await consent.listRecords();
        return {
          kind: "live",
          badgeState: "verified",
          badgeLabel: "✓ LIVE",
          metrics: [{ label: "records", value: String(r.records.length) }],
        };
      } catch (e) {
        if (isPlaneOffline(e)) return { kind: "offline", err: e };
        if (isMeshApiError(e) && e.status === 403) {
          return {
            kind: "live",
            badgeState: "attested",
            badgeLabel: "LIVE · access-gated",
            metrics: [{ label: "status", value: "operator credential required" }],
          };
        }
        return { kind: "error", message: e instanceof Error ? e.message : "probe failed" };
      }
    },
  },
];

/** Classify each of the four non-Transport planes once from a real, live read. */
function usePlaneRowStatus(): Record<string, PlaneRowStatus> {
  const [status, setStatus] = useState<Record<string, PlaneRowStatus>>(() =>
    Object.fromEntries(PLANE_ROWS.map((p) => [p.id, { kind: "loading" } as PlaneRowStatus])),
  );

  useEffect(() => {
    let alive = true;
    for (const p of PLANE_ROWS) {
      p.classify().then((result) => {
        if (!alive) return;
        setStatus((s) => ({ ...s, [p.id]: result }));
      });
    }
    return () => {
      alive = false;
    };
  }, []);

  return status;
}

/* ====================================================================== *
 * The living mesh — canvas node-map.                                     *
 * ====================================================================== */

interface Node {
  x: number; // normalised 0..1
  y: number;
  agency: string;
}

interface Edge {
  a: number;
  b: number;
}

/**
 * A small, fixed topology of agencies and cross-signs. These are LABELS for
 * the substrate's shape, not fabricated data: there is no participant-registry
 * endpoint up (Trust plane offline), so the map is explicitly an illustrative
 * topology. The packets riding the edges, however, are rate-bound to the live
 * Transport throughput delta — that motion is real.
 */
const NODES: Node[] = [
  { x: 0.5, y: 0.14, agency: "Civil Registry" },
  { x: 0.82, y: 0.34, agency: "Tax Authority" },
  { x: 0.78, y: 0.74, agency: "Health Ministry" },
  { x: 0.5, y: 0.9, agency: "Land Registry" },
  { x: 0.18, y: 0.72, agency: "Social Security" },
  { x: 0.18, y: 0.3, agency: "Customs" },
  { x: 0.5, y: 0.5, agency: "Mesh Sidecar" }, // hub — the one that's live
];

const HUB = 6;

const EDGES: Edge[] = [
  // every agency cross-signs with the hub (the live sidecar)
  { a: 0, b: HUB },
  { a: 1, b: HUB },
  { a: 2, b: HUB },
  { a: 3, b: HUB },
  { a: 4, b: HUB },
  { a: 5, b: HUB },
  // a few peer cross-signs around the ring
  { a: 0, b: 1 },
  { a: 1, b: 2 },
  { a: 3, b: 4 },
  { a: 4, b: 5 },
  { a: 5, b: 0 },
];

interface Packet {
  edge: number; // index into EDGES
  pos: number; // 0..1 along the edge
  dir: 1 | -1;
}

interface CssVars {
  bg: string;
  hair: string;
  hairStrong: string;
  ember: string;
  teal: string;
  indigo: string;
  fgFaint: string;
}

function readVars(el: HTMLElement): CssVars {
  const cs = getComputedStyle(el);
  const v = (n: string, fb: string) => cs.getPropertyValue(n).trim() || fb;
  return {
    bg: v("--bg", "#0A0E1A"),
    hair: v("--hair", "rgba(230,234,244,0.10)"),
    hairStrong: v("--hair-strong", "rgba(230,234,244,0.18)"),
    ember: v("--ember", "#E8915B"),
    teal: v("--teal", "#3DD8C4"),
    indigo: v("--indigo", "#6E8CFF"),
    fgFaint: v("--fg-faint", "rgba(230,234,244,0.55)"),
  };
}

/**
 * The canvas renderer. Live `packetRate` (packets spawned per second) is fed
 * via a ref so the animation loop reads the latest value without restarting.
 * Reduced-motion → exactly one still frame, no rAF loop.
 */
function LivingMesh({
  packetRateRef,
  reducedMotion,
}: {
  packetRateRef: React.MutableRefObject<number>;
  reducedMotion: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const vars = readVars(wrap);
    let raf = 0;
    let disposed = false;
    let packets: Packet[] = [];
    let spawnAccumulator = 0;
    let last = performance.now();

    function sizeToWrap() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = wrap!.getBoundingClientRect();
      canvas!.width = Math.max(1, Math.round(rect.width * dpr));
      canvas!.height = Math.max(1, Math.round(rect.height * dpr));
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: rect.width, h: rect.height };
    }

    function px(n: Node, w: number, h: number) {
      // inset so nodes/labels never clip the edges
      const padX = 26;
      const padY = 22;
      return {
        x: padX + n.x * (w - padX * 2),
        y: padY + n.y * (h - padY * 2),
      };
    }

    function draw(w: number, h: number) {
      ctx!.clearRect(0, 0, w, h);

      // edges (cross-signs) — thin structural hairlines
      for (const e of EDGES) {
        const a = px(NODES[e.a], w, h);
        const b = px(NODES[e.b], w, h);
        const toHub = e.a === HUB || e.b === HUB;
        ctx!.strokeStyle = toHub ? vars.hairStrong : vars.hair;
        ctx!.lineWidth = 1;
        ctx!.beginPath();
        ctx!.moveTo(a.x, a.y);
        ctx!.lineTo(b.x, b.y);
        ctx!.stroke();
      }

      // packets (data-in-motion) — ember, riding their edge
      for (const p of packets) {
        const e = EDGES[p.edge];
        const a = px(NODES[e.a], w, h);
        const b = px(NODES[e.b], w, h);
        const t = p.dir === 1 ? p.pos : 1 - p.pos;
        const x = a.x + (b.x - a.x) * t;
        const y = a.y + (b.y - a.y) * t;
        ctx!.fillStyle = vars.ember;
        ctx!.shadowColor = vars.ember;
        ctx!.shadowBlur = 8;
        ctx!.beginPath();
        ctx!.arc(x, y, 2.6, 0, Math.PI * 2);
        ctx!.fill();
        ctx!.shadowBlur = 0;
      }

      // nodes (agencies) — verified-teal ring on the live hub, indigo otherwise
      for (let i = 0; i < NODES.length; i++) {
        const n = NODES[i];
        const c = px(n, w, h);
        const isHub = i === HUB;
        const r = isHub ? 9 : 6;
        ctx!.fillStyle = vars.bg;
        ctx!.beginPath();
        ctx!.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx!.fill();
        ctx!.lineWidth = isHub ? 2 : 1.4;
        ctx!.strokeStyle = isHub ? vars.teal : vars.indigo;
        ctx!.beginPath();
        ctx!.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx!.stroke();

        // label
        ctx!.fillStyle = isHub ? vars.teal : vars.fgFaint;
        ctx!.font =
          '10px "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
        ctx!.textAlign = "center";
        ctx!.textBaseline = "top";
        ctx!.fillText(n.agency, c.x, c.y + r + 4);
      }
    }

    function step(now: number) {
      if (disposed) return;
      const dtRaw = (now - last) / 1000;
      last = now;
      // clamp dt so a backgrounded tab doesn't fling every packet across — this
      // is what makes the motion frame-rate-independent.
      const dt = Math.min(dtRaw, 0.05);

      const { w, h } = sizeToWrap();

      // spawn packets at the live rate (per second), accumulator-driven
      const rate = Math.max(0, packetRateRef.current);
      spawnAccumulator += rate * dt;
      // cap so we never spam the canvas if the rate spikes
      const maxSpawn = 4;
      let spawned = 0;
      while (spawnAccumulator >= 1 && spawned < maxSpawn) {
        spawnAccumulator -= 1;
        spawned++;
        const edge = Math.floor(Math.random() * EDGES.length);
        packets.push({
          edge,
          pos: 0,
          dir: Math.random() < 0.5 ? 1 : -1,
        });
      }
      // never let the accumulator run away when idle
      if (spawnAccumulator > 2) spawnAccumulator = 2;

      // advance packets (~0.55 edge-lengths / second)
      const speed = 0.55;
      for (const p of packets) p.pos += speed * dt;
      packets = packets.filter((p) => p.pos < 1);

      // keep a gentle ambient minimum so the mesh always feels alive without
      // implying traffic that isn't there: a single slow heartbeat packet.
      if (packets.length === 0 && rate === 0) {
        packets.push({ edge: 0, pos: 0, dir: 1 });
      }

      draw(w, h);
      raf = requestAnimationFrame(step);
    }

    if (reducedMotion) {
      // one still frame: edges + nodes, no packets, no loop.
      const { w, h } = sizeToWrap();
      packets = [];
      draw(w, h);
      const onResize = () => {
        const s = sizeToWrap();
        draw(s.w, s.h);
      };
      window.addEventListener("resize", onResize);
      return () => {
        disposed = true;
        window.removeEventListener("resize", onResize);
      };
    }

    raf = requestAnimationFrame(step);
    const onResize = () => sizeToWrap();
    window.addEventListener("resize", onResize);
    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [packetRateRef, reducedMotion]);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        height: "min(46vh, 380px)",
        minHeight: "240px",
      }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={
          reducedMotion
            ? "Mesh topology: seven agency nodes cross-signed around a live sidecar hub (motion disabled)."
            : "Living mesh: agency nodes connected by cross-sign edges, with signed packets in motion at the live throughput rate."
        }
        style={{ display: "block", width: "100%", height: "100%" }}
      />
    </div>
  );
}

/* ====================================================================== *
 * Helpers for the live throughput delta → packet rate.                   *
 * ====================================================================== */

/** Convert a counter's per-poll delta into a smoothed packets-per-second rate. */
function useThroughputRate(
  samples: MetricSample[] | null,
  intervalMs: number,
): { rate: number; lastDelta: number } {
  const prevRef = useRef<number | null>(null);
  const [state, setState] = useState({ rate: 0, lastDelta: 0 });

  useEffect(() => {
    if (!samples) return;
    const total = sumMetric(samples, TRANSPORT_METRICS.requestsTotal);
    if (!Number.isFinite(total)) return;
    const prev = prevRef.current;
    prevRef.current = total;
    if (prev === null) return; // need two samples to make a delta
    const delta = Math.max(0, total - prev);
    // spread the delta's packets across the poll window, plus a low ambient
    // floor so a quiet-but-healthy mesh still shows life.
    const perSec = delta / (intervalMs / 1000);
    const rate = Math.min(6, 0.25 + perSec * 1.5);
    setState({ rate, lastDelta: delta });
  }, [samples, intervalMs]);

  return state;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/* ====================================================================== *
 * The view.                                                              *
 * ====================================================================== */

const POLL_MS = 5000;

export default function Overview({ role }: { role: Role }) {
  const health = useHealthPoll(POLL_MS);
  const { samples, error, lastUpdated, refresh } = useMetricsPoll(POLL_MS);
  const rowStatus = usePlaneRowStatus();
  const reducedMotion = usePrefersReducedMotion();
  const [claim, setClaim] = useState<Claim | null>(null);

  // Live throughput → packet rate for the living mesh.
  const { rate, lastDelta } = useThroughputRate(samples, POLL_MS);
  const packetRateRef = useRef(0);
  packetRateRef.current = health.healthy ? rate : 0;

  // Top-line scalars (null when unreadable — never a fabricated zero).
  const metricsDown = !!error && !samples;
  const requestsTotal = samples
    ? sumMetric(samples, TRANSPORT_METRICS.requestsTotal)
    : null;
  const verifications = samples
    ? sumMetric(samples, TRANSPORT_METRICS.signatureVerifications)
    : null;
  const queuePending = samples
    ? sumMetric(samples, TRANSPORT_METRICS.queuePending)
    : null;
  const replayDetections = samples
    ? sumMetric(samples, TRANSPORT_METRICS.replayDetections)
    : null;

  // rolling series for sparklines
  const throughputSeries = useDerivedSeries(samples, (s) =>
    sumMetric(s, TRANSPORT_METRICS.requestsTotal),
  );
  const queueSeries = useDerivedSeries(samples, (s) =>
    sumMetric(s, TRANSPORT_METRICS.queuePending),
  );

  /* ---- The headline verification claim — substantiated or not. ---- */
  const verifyState: VerifyState = useMemo(() => {
    if (!health.healthy) return "offline";
    if (metricsDown) return "failed";
    if (verifications === null) return "unknown";
    return verifications > 0 ? "verified" : "pending";
  }, [health.healthy, metricsDown, verifications]);

  const verifyClaim = useMemo<Claim>(() => {
    const reqLine = findMetric(samples ?? [], TRANSPORT_METRICS.requestsTotal);
    const verLine = findMetric(
      samples ?? [],
      TRANSPORT_METRICS.signatureVerifications,
    );
    const evidenceReady = health.healthy && !metricsDown && samples;
    return {
      label: "Transport signature verification",
      verdict: verifyState,
      evidence: evidenceReady
        ? {
            algorithm: "ed25519 (RFC 9421 message signatures)",
            specRef: "Part IV §27 — RFC 9421 signed dispatch",
            source: `${MESH_BASE}/v1/metrics`,
            detail: [
              {
                label: "signature_verifications_total",
                value: String(verLine?.value ?? verifications ?? "—"),
              },
              {
                label: "requests_total",
                value: String(reqLine?.value ?? requestsTotal ?? "—"),
              },
              {
                label: "healthz",
                value: `HTTP ${health.status}${
                  health.latencyMs !== undefined ? ` · ${health.latencyMs}ms` : ""
                }`,
              },
              {
                label: "last poll",
                value: lastUpdated
                  ? new Date(lastUpdated).toLocaleTimeString()
                  : "—",
              },
            ],
          }
        : undefined, // no live data ⇒ unsubstantiated, by law
    };
  }, [
    samples,
    health,
    metricsDown,
    verifyState,
    verifications,
    requestsTotal,
    lastUpdated,
  ]);

  function inspect(c: Claim) {
    setClaim(c);
  }

  /** Build the per-metric "inspect its source line" claim. */
  function metricClaim(
    label: string,
    series: string,
    value: number | null,
    specRef: string,
  ): Claim {
    const line = findMetric(samples ?? [], series);
    return {
      label,
      verdict: value === null ? "unknown" : "verified",
      evidence:
        value === null
          ? undefined
          : {
              specRef,
              source: `${MESH_BASE}/v1/metrics`,
              detail: [
                { label: series, value: String(line?.value ?? value) },
                {
                  label: "read at",
                  value: lastUpdated
                    ? new Date(lastUpdated).toLocaleTimeString()
                    : "—",
                },
              ],
            },
    };
  }

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-ember">Overview</span>
          <span>Mesh · Parts I–II</span>
        </div>
        <h1 className="view-header__title">Mesh at a glance</h1>
        <p className="view-header__sub">
          The single pane: live sidecar health, Transport throughput, and the
          honest status of every plane. Every figure here links to its proof.
          Current role: <strong>{role}</strong>.
        </p>
      </header>

      {/* -------------------------------------------------------------- *
       *  The living mesh + headline verification badge.                *
       * -------------------------------------------------------------- */}
      <div
        className="panel"
        style={{
          marginBottom: "var(--space-5)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-4)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "var(--space-4)",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: "20px" }}>The living mesh</h2>
            <p
              style={{
                color: "var(--fg-muted)",
                margin: "6px 0 0",
                maxWidth: "56ch",
                fontSize: "14px",
              }}
            >
              Agencies cross-sign around the live sidecar hub. Packets in motion
              ride at the measured throughput rate — when{" "}
              <span className="accent-ember">requests</span> arrive, you see
              them move. Topology is illustrative (the participant registry is
              offline); the motion is real.
            </p>
          </div>
          <VerifyBadge
            state={verifyState}
            label={
              verifyState === "verified"
                ? "Signatures verified"
                : verifyState === "pending"
                  ? "Awaiting first dispatch"
                  : verifyState === "offline"
                    ? "Sidecar offline"
                    : verifyState === "failed"
                      ? "Metrics unreadable"
                      : "Unsubstantiated"
            }
            onInspect={() => inspect(verifyClaim)}
          />
        </div>

        <LivingMesh
          packetRateRef={packetRateRef}
          reducedMotion={reducedMotion}
        />

        <div
          className="mono"
          style={{
            fontSize: "11px",
            color: "var(--fg-faint)",
            display: "flex",
            gap: "var(--space-4)",
            flexWrap: "wrap",
          }}
        >
          <span>
            <span style={{ color: "var(--teal)" }}>●</span> live hub (sidecar)
          </span>
          <span>
            <span style={{ color: "var(--indigo)" }}>●</span> agency (illustrative)
          </span>
          <span>
            <span style={{ color: "var(--ember)" }}>●</span> signed packet ·{" "}
            {health.healthy
              ? lastDelta > 0
                ? `+${lastDelta} since last poll`
                : reducedMotion
                  ? "motion off"
                  : "idle heartbeat"
              : "halted (offline)"}
          </span>
        </div>
      </div>

      {/* -------------------------------------------------------------- *
       *  Top-line transport scalars (live).                            *
       * -------------------------------------------------------------- */}
      {metricsDown ? (
        <div style={{ marginBottom: "var(--space-5)" }}>
          <PlaneState
            kind="error"
            title="Transport metrics unreadable"
            message={`Could not read ${MESH_BASE}/v1/metrics — shown loudly rather than as zeros. ${
              error?.message ?? ""
            }`}
            specRef="Part IV §24–§31"
            onRetry={refresh}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "var(--space-4)",
            marginBottom: "var(--space-5)",
          }}
        >
          <MetricTile
            label="Requests total"
            value={requestsTotal}
            unit="req"
            accent="var(--ember)"
            series={throughputSeries}
            source={TRANSPORT_METRICS.requestsTotal}
            onInspect={() =>
              inspect(
                metricClaim(
                  "Requests total",
                  TRANSPORT_METRICS.requestsTotal,
                  requestsTotal,
                  "Part IV §24 — transport request counter",
                ),
              )
            }
          />
          <MetricTile
            label="Signatures verified"
            value={verifications}
            unit="ok"
            accent="var(--teal)"
            source={TRANSPORT_METRICS.signatureVerifications}
            onInspect={() => inspect(verifyClaim)}
          />
          <MetricTile
            label="Queue pending"
            value={queuePending}
            unit="queued"
            accent="var(--gold)"
            series={queueSeries}
            source={TRANSPORT_METRICS.queuePending}
            onInspect={() =>
              inspect(
                metricClaim(
                  "Store-and-forward queue depth",
                  TRANSPORT_METRICS.queuePending,
                  queuePending,
                  "Part IV §29 — store-and-forward queue",
                ),
              )
            }
          />
          <MetricTile
            label="Replay detections"
            value={replayDetections}
            unit="blocked"
            accent="var(--red)"
            loud
            source={TRANSPORT_METRICS.replayDetections}
            onInspect={() =>
              inspect(
                metricClaim(
                  "Replay detections",
                  TRANSPORT_METRICS.replayDetections,
                  replayDetections,
                  "Part IV §28 — replay protection",
                ),
              )
            }
          />
        </div>
      )}

      {/* -------------------------------------------------------------- *
       *  The plane STACK — five planes as layered strata of one         *
       *  substrate, in architecture order. Transport is the one lit,    *
       *  raised band; the rest are calm dormant strata (an expected     *
       *  pre-pilot state). A plane that responds unexpectedly is loud.  *
       * -------------------------------------------------------------- */}
      <div style={{ marginBottom: "var(--space-3)" }}>
        <div
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
          }}
        >
          Plane health
        </div>
      </div>

      <div
        className="plane-stack"
        role="list"
        aria-label="The five mesh planes, layered top to bottom in architecture order"
      >
        {PLANE_ORDER.map((id) => {
          // Transport — the one LIVE, lit band.
          if (id === "transport") {
            const transportState: StratumState = {
              kind: "live",
              badge: (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <VerifyBadge
                    state={!health.healthy ? "offline" : metricsDown ? "failed" : "verified"}
                    size="sm"
                    label={
                      !health.healthy
                        ? "OFFLINE"
                        : metricsDown
                          ? "METRICS DOWN"
                          : "✓ ONLINE"
                    }
                    onInspect={() =>
                      inspect({
                        label: "Transport sidecar health",
                        verdict: health.healthy
                          ? metricsDown
                            ? "failed"
                            : "verified"
                          : "offline",
                        evidence: health.healthy
                          ? {
                              specRef: "Part IV §24–§31",
                              source: `${MESH_BASE}/healthz`,
                              detail: [
                                { label: "healthz", value: `HTTP ${health.status}` },
                                {
                                  label: "latency",
                                  value:
                                    health.latencyMs !== undefined
                                      ? `${health.latencyMs} ms`
                                      : "—",
                                },
                                {
                                  label: "checked at",
                                  value: health.checkedAt
                                    ? new Date(health.checkedAt).toLocaleTimeString()
                                    : "—",
                                },
                              ],
                            }
                          : undefined,
                      })
                    }
                  />
                  <span className="mono" style={{ fontSize: "11px", color: "var(--fg-faint)" }}>
                    {health.latencyMs !== undefined ? `${health.latencyMs} ms` : "—"}
                  </span>
                </span>
              ),
              metrics: [
                {
                  label: "requests",
                  value: requestsTotal === null ? "—" : requestsTotal.toLocaleString(),
                  onInspect: () =>
                    inspect(
                      metricClaim(
                        "Requests total",
                        TRANSPORT_METRICS.requestsTotal,
                        requestsTotal,
                        "Part IV §24 — transport request counter",
                      ),
                    ),
                },
                {
                  label: "signatures verified",
                  value: verifications === null ? "—" : verifications.toLocaleString(),
                  onInspect: () => inspect(verifyClaim),
                },
                {
                  label: "queue pending",
                  value: queuePending === null ? "—" : String(queuePending),
                  onInspect: () =>
                    inspect(
                      metricClaim(
                        "Store-and-forward queue depth",
                        TRANSPORT_METRICS.queuePending,
                        queuePending,
                        "Part IV §29 — store-and-forward queue",
                      ),
                    ),
                },
              ],
            };
            return (
              <div role="listitem" key={id}>
                <PlaneStratum name="Transport" refs="Plane IV · §24–§31" state={transportState} />
              </div>
            );
          }

          // The four other planes — each now a real, live read.
          const p = PLANE_ROWS.find((x) => x.id === id)!;
          const st = rowStatus[p.id];

          let state: StratumState;
          if (st && st.kind === "error") {
            // Reserved LOUD case: an unexpected, unclassified failure.
            state = { kind: "failure", message: st.message, endpoints: p.endpoints };
          } else if (st && st.kind === "live") {
            state = {
              kind: "live",
              badge: (
                <VerifyBadge state={st.badgeState} size="sm" label={st.badgeLabel} />
              ),
              metrics: st.metrics.map((m) => ({ label: m.label, value: m.value })),
            };
          } else {
            // offline (or still loading) → calm dormant; the service name comes
            // from the PlaneOfflineError once probed, else the spec default.
            const service =
              st && st.kind === "offline" ? st.err.service : "awaiting probe";
            state = { kind: "dormant", service, endpoints: p.endpoints };
          }

          return (
            <div role="listitem" key={id}>
              <PlaneStratum name={p.label} refs={p.plane} state={state} />
            </div>
          );
        })}
      </div>

      <p
        className="mono"
        style={{
          marginTop: "var(--space-5)",
          fontSize: "11px",
          color: "var(--fg-faint)",
          maxWidth: "70ch",
        }}
      >
        These are the five planes as layers of one substrate. Each is lit
        LIVE from its own real read — an empty result (no anchor yet, an
        empty ledger) or a correctly-gated 403 (Consent) still counts as
        "live": the service answered honestly, it just has nothing to show or
        correctly refused. A plane that is unreachable sits dormant — an
        expected pre-pilot state, not a failure. Each dormant layer discloses
        the endpoints it will bind to. Nothing is fabricated.
      </p>

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}
